import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { clearScrollPosition, rememberScrollPosition } from '../actions/utility-actions';
import { AppUtils } from '../utils/app-utils';

/**
 * Provide a means of persisting the scroll position and provides
 * a method to restore the screen to that position.
 * 
 * @param scrollStateName The key that you want to save the position to in redux.
 * This should be 'page' specific
 * @param pageMatch If the next route has an indexOf hit for this parameter, the 
 * position will be stored in redux at the above key.
 * @returns Returns a function that will scroll the page to the position found in
 * redux for the given key
 */
export const useScrollMemory = (scrollStateName : string, pageMatch : string) => {

    const dispatch      = useDispatch();
    const store         = useStore();
    const [initRoute, setInitRoute] = React.useState<string>();

    /**
     * Save off the initial pathname of the page
     * for later comparisons
     */
    React.useEffect(() => {
        setInitRoute(document.location.pathname);
    }, []);

    /**
     * Pull the provided scroll state variable and
     * if present, move the window to that location
     * and then clear the saved state.
     */
    const scrollTo = () => {
        const savedState = store.getState().utility.scrollState[scrollStateName];

        if (!AppUtils.IsEmpty(savedState)) {
            window.scroll({top: savedState.y + 90, left: 0, behavior: 'auto' });
            
            const payload = clearScrollPosition(scrollStateName);
            dispatch(payload);
        }
    };

    React.useEffect(() => {

        // If the next path matches the provided path
        // save off the current position
        return () => {

            const newRoute = document.location.pathname;
            if (initRoute && newRoute !== initRoute) {
                if (document.location.pathname.indexOf(pageMatch) > -1) {

                    const payload = rememberScrollPosition(
                        window.pageXOffset,
                        window.pageYOffset - 90,
                        scrollStateName
                    );

                    dispatch(payload);
                }
                else {
                    const payload = clearScrollPosition(scrollStateName);
                    dispatch(payload);
                }
            }
            
        };
    });
    
    return scrollTo;
    
};
